import { useState } from 'react';
import { setAttr } from '@directus/visual-editing';
import type { EditConfig } from '@directus/visual-editing/types';

/**
 * Directus Visual Editor bridge helpers.
 *
 * The inline editing layer is backed by the official Directus 12 Visual Editor
 * (`@directus/visual-editing`) instead of an in-app WYSIWYG editor. Editable
 * elements are marked with a `data-directus` attribute produced by `setAttr`;
 * the actual editing happens in the Directus Studio drawer when the page is
 * loaded inside the Visual Editor iframe.
 */

/** Resolve the configured Directus instance URL. */
export function getDirectusUrl(): string {
  return import.meta.env.VITE_DIRECTUS_URL || 'http://localhost:8065';
}

/**
 * Whether the page is currently being rendered inside the Directus Studio
 * Visual Editor.
 *
 * Editing is allowed ONLY inside the Directus UI: when the site is browsed
 * normally (even by an authenticated admin) this returns false, so no
 * `data-directus` markers are emitted and no edit affordances are shown. The
 * page is considered "inside the editor" when it is embedded in an iframe whose
 * parent is the configured Directus instance (or any Directus `/admin/` route).
 */
export function isInsideVisualEditor(): boolean {
  try {
    if (typeof window === 'undefined') return false;
    // The Directus Studio always embeds the site in an iframe, while normal
    // (top-level) browsing never is. This is the only signal available
    // SYNCHRONOUSLY at mount — and being synchronous matters: the markers must
    // exist before `@directus/visual-editing` scans the DOM, or no frames are
    // drawn. We deliberately don't gate on `document.referrer`: it becomes the
    // app's own origin after any in-iframe navigation, which silently breaks
    // detection. Emitting markers in a non-Directus iframe is harmless — the
    // library only draws overlays / opens the drawer once it connects to a
    // real Studio, so editing still happens ONLY inside the Directus UI.
    return window.self !== window.top;
  } catch {
    return false;
  }
}

/**
 * Hook variant of {@link isInsideVisualEditor}. Whether the page is inside the
 * Studio iframe is fixed for the component's whole lifetime (the iframe check is
 * synchronous and never flips), so this just memoizes the one-time read.
 */
export function useIsVisualEditor(): boolean {
  const [inEditor] = useState(isInsideVisualEditor);
  return inEditor;
}

export interface EditAttrOptions {
  collection: string;
  item: string | number | null;
  fields?: string | string[];
  mode?: 'drawer' | 'modal' | 'popover';
}

/**
 * Build the `data-directus` attribute value that marks an element as editable
 * inside the Directus Visual Editor. Returns an empty string when no item id is
 * available so callers can spread it safely.
 */
export function buildEditAttr({ collection, item, fields, mode = 'drawer' }: EditAttrOptions): string {
  if (item === null || item === undefined || item === '') {
    return '';
  }

  const config: EditConfig = {
    collection,
    item,
    mode,
    ...(fields ? { fields: Array.isArray(fields) ? fields : [fields] } : {}),
  };

  return setAttr(config);
}

/**
 * Convenience helper that returns the props object to spread onto a host
 * element. When editing is disabled (or no item is set) it returns an empty
 * object, so the element renders as plain, non-editable content.
 */
export function editableProps(
  enabled: boolean,
  options: EditAttrOptions,
): { 'data-directus'?: string } {
  if (!enabled) {
    return {};
  }

  const attr = buildEditAttr(options);
  return attr ? { 'data-directus': attr } : {};
}
